import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import api, { formatTL } from "@/lib/api";
import { useLang } from "@/contexts/LangContext";
import { useCart } from "@/contexts/CartContext";
import { toast } from "sonner";
import { Minus, Plus, Check, MessageCircle } from "lucide-react";
import FavoriteButton from "@/components/FavoriteButton";
import ProductCard from "@/components/ProductCard";
import ProductGallery from "@/components/ProductGallery";

export default function ProductDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t, lang } = useLang();
  const { addItem } = useCart();
  const [product, setProduct] = useState(null);
  const [qty, setQty] = useState(1);
  const [related, setRelated] = useState([]);
  const [site, setSite] = useState({});

  useEffect(() => {
    (async () => {
      // Try slug first, then id
      let data = null;
      try {
        const slugRes = await api.get(`/products/by-slug/${id}`);
        data = slugRes.data;
      } catch {
        const idRes = await api.get(`/products/${id}`);
        data = idRes.data;
        // If user came via raw id, redirect to slug URL (301-equivalent client-side)
        if (data.seo_slug && data.seo_slug !== id) {
          navigate(`/products/${data.seo_slug}`, { replace: true });
        }
      }
      setProduct(data);
      setQty(1);
      const r = await api.get(`/products?category_id=${data.category_id}&limit=4`);
      setRelated((r.data.items || []).filter((p) => p.id !== data.id).slice(0, 4));
    })();
    api.get("/settings/site").then((r) => setSite(r.data || {})).catch(() => {});
  }, [id, navigate]);

  // Inject Product JSON-LD for Google rich results
  useEffect(() => {
    if (!product) return;
    const tag = document.createElement("script");
    tag.type = "application/ld+json";
    const imgs = (product.images || []).map((u) => u.startsWith("http") ? u : `${process.env.REACT_APP_BACKEND_URL}${u}`);
    const avail = product.stock_status === "out_of_stock"
      ? "https://schema.org/OutOfStock"
      : product.stock_status === "coming_soon" ? "https://schema.org/PreOrder"
      : "https://schema.org/InStock";
    const ld = {
      "@context": "https://schema.org/",
      "@type": "Product",
      name: product.name_tr,
      description: product.description_tr || product.name_tr,
      sku: product.sku,
      mpn: product.sku,
      brand: { "@type": "Brand", name: product.brand || "Uzungöl Kuyumculuk" },
      image: imgs.length ? imgs : undefined,
      offers: {
        "@type": "Offer",
        priceCurrency: "TRY",
        price: product.discounted_price || product.price,
        availability: avail,
        url: typeof window !== "undefined" ? window.location.href : undefined,
        itemCondition: "https://schema.org/NewCondition",
      },
    };
    tag.text = JSON.stringify(ld);
    document.head.appendChild(tag);
    return () => { document.head.removeChild(tag); };
  }, [product]);

  const waUrl = (() => {
    const phone = (site.whatsapp_number || "+905368147467").replace(/\D/g, "");
    if (!product) return `https://wa.me/${phone}`;
    const text = encodeURIComponent(
      `Merhaba,\n\nAşağıdaki ürün hakkında bilgi almak istiyorum:\n\n${product.name_tr}\nSKU: ${product.sku}\n${typeof window !== "undefined" ? window.location.href : ""}\n\nLütfen iletişime geçer misiniz?`
    );
    return `https://wa.me/${phone}?text=${text}`;
  })();

  if (!product) return <div className="px-6 py-20 text-center text-sm">{t("common.loading")}</div>;
  const name = lang === "tr" ? product.name_tr : product.name_en;
  const desc = lang === "tr" ? product.description_tr : product.description_en;
  const hasDiscount = product.discounted_price && product.discounted_price < product.price;

  return (
    <div data-testid="product-detail-page" className="px-6 lg:px-16 py-12 max-w-[1440px] mx-auto">
      <div className="grid lg:grid-cols-2 gap-12 lg:gap-20">
        <ProductGallery images={product.images || []} alt={name} />

        <div className="space-y-6">
          <div>
            <p className="text-xs uppercase tracking-[0.3em] text-[#C8A96A]">{product.code || product.sku}</p>
            <h1 data-testid="product-name" className="font-heading text-4xl lg:text-5xl mt-2 leading-tight">{name}</h1>
          </div>

          <div className="flex items-baseline gap-4 pb-6 border-b border-border">
            {hasDiscount ? (
              <>
                <span className="font-finance text-3xl text-[#C8A96A]">{formatTL(product.discounted_price)}</span>
                <span className="font-finance text-base text-[#A1A1AA] line-through">{formatTL(product.price)}</span>
              </>
            ) : (
              <span data-testid="product-price" className="font-finance text-3xl">{formatTL(product.price)}</span>
            )}
          </div>

          {desc && (
            <div>
              <h3 className="text-xs uppercase tracking-[0.2em] mb-3 font-medium">{t("product.description")}</h3>
              <p className="text-sm leading-relaxed text-[#52525B]">{desc}</p>
            </div>
          )}

          {product.features?.length > 0 && (
            <div>
              <h3 className="text-xs uppercase tracking-[0.2em] mb-3 font-medium">{t("product.features")}</h3>
              <ul className="space-y-1.5">
                {product.features.map((f, i) => (
                  <li key={i} className="text-sm text-[#52525B] flex items-center gap-2"><Check size={14} className="text-[#C8A96A]" />{f}</li>
                ))}
              </ul>
            </div>
          )}

          <div className="pt-4 space-y-4">
            <div className="flex items-center gap-3">
              <span className="text-xs uppercase tracking-[0.2em]">{t("product.stock")}:</span>
              <span data-testid="product-stock" className={`text-sm ${product.stock > 0 ? "text-[#10B981]" : "text-[#EF4444]"}`}>
                {product.stock > 0 ? `${t("product.in_stock")} (${product.stock})` : t("product.out_of_stock")}
              </span>
            </div>

            <div className="flex items-center gap-4">
              <div className="flex items-center border border-[#0A0A0A]">
                <button data-testid="qty-decrease" onClick={() => setQty(Math.max(1, qty - 1))} className="px-3 py-3"><Minus size={14} /></button>
                <span data-testid="qty-value" className="px-6 font-finance text-sm">{qty}</span>
                <button data-testid="qty-increase" onClick={() => setQty(qty + 1)} className="px-3 py-3"><Plus size={14} /></button>
              </div>
              <button data-testid="add-to-cart-btn" disabled={product.stock <= 0}
                onClick={() => { addItem(product, qty); toast.success(lang === "tr" ? "Sepete eklendi" : "Added to cart"); }}
                className="flex-1 bg-[#0A0A0A] hover:bg-[#C8A96A] text-white py-4 text-xs uppercase tracking-[0.25em] transition-colors disabled:opacity-40">
                {t("product.add_to_cart")}
              </button>
              <FavoriteButton productId={product.id} productName={lang === "tr" ? product.name_tr : product.name_en} variant="detail" />
            </div>
            <a data-testid="whatsapp-inquiry-btn" href={waUrl} target="_blank" rel="noopener noreferrer"
              className="w-full border border-[#25D366] text-[#25D366] hover:bg-[#25D366] hover:text-white py-3 text-xs uppercase tracking-[0.25em] transition-colors inline-flex items-center justify-center gap-2">
              <MessageCircle size={14} /> {lang === "tr" ? "WhatsApp ile Sor" : "Ask on WhatsApp"}
            </a>
          </div>
        </div>
      </div>

      {related.length > 0 && (
        <div className="mt-24">
          <h2 className="font-heading text-3xl lg:text-4xl mb-10">{t("product.related")}</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 lg:gap-10">
            {related.map((p) => <ProductCard key={p.id} product={p} />)}
          </div>
        </div>
      )}
    </div>
  );
}
